import { FunctionComponent } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { APP_NOTICE_BUTTER, APP_NOTICE_INK, IAppPalette } from './appPalette';
import { schoolInitials } from './schoolInitials';

interface IAppScreenPreviewProps {
  palette: IAppPalette;
  schoolName: string;
  imageUrl?: string;
  /** Undefined while the logo has not been measured — the app's own default is compact. */
  isWide?: boolean;
}

/**
 * What is actually on a dancer's screen: the school's header, and a notice under it.
 *
 * The header alone would be a thinner illustration than the decision deserves. A logo is judged
 * against what surrounds it, and a notice is the one thing a dancer sees every time they open the
 * app — so a mark that vanishes on a butter note, or ink that disappears on a dark page, shows up
 * here rather than after a school has switched their whole studio over.
 *
 * The notice keeps its own paper on both grounds, because that is what the app does: notice colours
 * are chosen by the school and stored as resolved values, so they do not follow the theme.
 */
export const AppScreenPreview: FunctionComponent<IAppScreenPreviewProps> = (props: IAppScreenPreviewProps) => {

  const { palette, schoolName, imageUrl, isWide } = props;

  const renderHeader = () => {
    if (!imageUrl) {
      // The app's own no-logo fallback: initials as a soft teal seal, never bare type.
      return (
        <Stack direction='row' alignItems='center' columnGap={1.25}>
          <Box sx={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            backgroundColor: `${palette.accent}1F`, color: palette.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700
          }}>
            {schoolInitials(schoolName)}
          </Box>
          <Typography noWrap sx={{ color: palette.ink, fontSize: 13, fontWeight: 600 }}>{schoolName}</Typography>
        </Stack>
      );
    }

    if (isWide) {
      return <Box component='img' src={imageUrl} alt='' sx={{ maxHeight: 34, maxWidth: '100%', objectFit: 'contain' }} />;
    }

    return (
      <Stack direction='row' alignItems='center' columnGap={1.25} sx={{ minWidth: 0 }}>
        <Box component='img' src={imageUrl} alt='' sx={{ height: 26, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
        <Typography noWrap sx={{ color: palette.ink, fontSize: 13, fontWeight: 600, minWidth: 0 }}>{schoolName}</Typography>
      </Stack>
    );
  };

  return (
    <>
      {renderHeader()}

      <Box sx={{ position: 'relative', mt: 0.5 }}>
        {/* The bit of tape that makes a notice read as pinned rather than as another card. */}
        <Box sx={{
          position: 'absolute', top: -5, left: '50%', width: 40, height: 9,
          transform: 'translateX(-50%) rotate(-2.5deg)',
          backgroundColor: '#FFFFFF8C', border: '1px solid #FFFFFF59'
        }} />
        <Stack sx={{ backgroundColor: APP_NOTICE_BUTTER, borderRadius: 1.5, px: 1.25, pt: 1.75, pb: 1.25 }} rowGap={0.5}>
          <Box sx={{ height: 5, width: '62%', borderRadius: 1, backgroundColor: `${APP_NOTICE_INK}59` }} />
          <Box sx={{ height: 4, width: '92%', borderRadius: 1, backgroundColor: `${APP_NOTICE_INK}2E` }} />
          <Box sx={{ height: 4, width: '78%', borderRadius: 1, backgroundColor: `${APP_NOTICE_INK}2E` }} />
        </Stack>
      </Box>

      {/* A class card, so the page shows the app's own surface as well as its page. */}
      <Stack
        sx={{
          backgroundColor: palette.surface,
          border: `1px solid ${palette.hairline}`,
          borderRadius: 1.5,
          px: 1.25,
          py: 1.25,
          transition: 'background-color 0.25s ease'
        }}
        rowGap={0.5}
      >
        <Box sx={{ height: 5, width: '54%', borderRadius: 1, backgroundColor: palette.ink, opacity: 0.75 }} />
        <Box sx={{ height: 4, width: '38%', borderRadius: 1, backgroundColor: palette.medium, opacity: 0.7 }} />
      </Stack>
    </>
  );
};
